export const MetdatataFieldKeys = [
  'name',
  'gabc-copyright',
  'score-copyright',
  'author',
  'language',
  'mode',
  'mode-modifier',
  'mode-differentia',
  'annotation',
  'staff-lines',
  'nabc-lines',
  'oricuss-orientation',
  'office-part',
  'occasion',
  'meter',
  'commentary',
  'arranger',
  'date',
  'manuscript',
  'manuscript-reference',
  'manuscript-storage-place',
  'book',
  'transcriber',
  'transcription-date',
  'user-notes',
  'initial',
] as const;
export type MetdatataFieldKey = typeof MetdatataFieldKeys[number];

class GregorioMetadataField {
  name: MetdatataFieldKey;
  constructor(name: MetdatataFieldKey) {
    this.name = name;
  }
}

export class GregorioMetadataNumericField extends GregorioMetadataField {
  value: number;
  constructor(name: MetdatataFieldKey, value: number) {
    super(name);
    this.value = value;
  }
}

export class GregorioMetadataStringField extends GregorioMetadataField {
  value: string;
  constructor(name: MetdatataFieldKey, value: string) {
    super(name);
    this.value = value;
  }
}

export class GregorioMetadataBooleanField extends GregorioMetadataField {
  value: boolean;
  constructor(name: MetdatataFieldKey, value: boolean) {
    super(name);
    this.value = value;
  }
}

export class GregorioMetadataArrayField extends GregorioMetadataField {
  value: string[];
  constructor(name: MetdatataFieldKey, value: string[]) {
    super(name);
    this.value = value;
  }
}

export class GregorioMetadata {
  _fields: GregorioMetadataField[] = [
    new GregorioMetadataStringField('name', ''),
    new GregorioMetadataStringField('gabc-copyright', ''),
    new GregorioMetadataStringField('score-copyright', ''),
    new GregorioMetadataStringField('author', ''),
    new GregorioMetadataStringField('language', ''),
    new GregorioMetadataStringField('mode', ''), // supported
    new GregorioMetadataStringField('mode-modifier', ''),
    new GregorioMetadataStringField('mode-differentia', ''),
    new GregorioMetadataArrayField('annotation', []), // supported
    new GregorioMetadataNumericField('staff-lines', 4),
    new GregorioMetadataNumericField('nabc-lines', 1),
    new GregorioMetadataBooleanField('oricuss-orientation', false),
    // new GregorioMetadataNumericField('def_mn', 0),
    new GregorioMetadataStringField('office-part', ''),
    new GregorioMetadataStringField('occasion', ''),
    new GregorioMetadataStringField('meter', ''),
    new GregorioMetadataStringField('commentary', ''), // supported
    new GregorioMetadataStringField('arranger', ''),
    new GregorioMetadataStringField('date', ''),
    new GregorioMetadataStringField('manuscript', ''),
    new GregorioMetadataStringField('manuscript-reference', ''),
    new GregorioMetadataStringField('manuscript-storage-place', ''),
    new GregorioMetadataStringField('book', ''),
    new GregorioMetadataStringField('transcriber', ''),
    new GregorioMetadataStringField('transcription-date', ''),
    new GregorioMetadataStringField('user-notes', ''),
    new GregorioMetadataNumericField('initial', 1), // supported
  ];

  constructor(metadata: string | undefined) {
    if (metadata === undefined) {
      return;
    }

    const rows = metadata.split(';').filter(row => row.trim().length > 0);

    for (const row of rows) {
      const buf = row.split(':');
      if (buf.length !== 2) {
        continue;
      }
      
      const key = buf[0].trim();
      const value = buf[1].trim();

      const field = this._fields.find(field => field.name === key);

      if (field instanceof GregorioMetadataStringField) {
        field.value = value;
      } else if (field instanceof GregorioMetadataNumericField) {
        field.value = parseInt(value);
      } else if (field instanceof GregorioMetadataBooleanField) {
        field.value = value === 'true';
      } else if (field instanceof GregorioMetadataArrayField) {
        field.value.push(value);
      } else {
        console.log('Unknown metadata field type: ' + key);
      }
    }
  }

  getField<T>(name: MetdatataFieldKey): T {
    const field = this._fields.find(field => field.name === name);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (field as any).value as T;
  }
}
